// src/utils/carteleria/calendarioNegocio.js
//
// FASE 1 del rediseño de Cartelería (docs/decisiones/2026-10-06-carteleria-rediseno-diseno.md).
//
// Motor PURO (sin DB, sin red, sin React, sin Date.now) que responde, para UNA fila de
// `negocios_carteleria`, "qué vale este proyecto y cuándo":
//
//   a) `devengo`        — por mes y por componente (gestion / contenidos / opex), con período y monto.
//   b) `hardwareCapex`  — el evento de venta de hardware CAPEX (fecha, monto, moneda, tramos y otros).
//   c) `lineas`         — las líneas a facturar/cobrar (gestión+contenidos mensual, opex mensual,
//                         tramos de hardware), con lo ya facturado según `meses_facturacion`.
//
// REGLAS DE DISEÑO
//  * TODO en la MONEDA ORIGINAL de cada componente. Acá NO se convierte nada: la conversión (UF/USD →
//    local, Criterio 1) la aplica cada superficie con su helper único. Prohibido importar
//    exchangeRateService / useCurrency en este archivo.
//  * DOS MODOS (F2). Los campos del §3 del plano (`fecha_fin`, `modo_precio_gestion`,
//    `monto_total_gestion`, `forma_facturacion`, `facturacion_cada_n_meses`) se leen CUANDO ESTÁN.
//    - modo "legado": sin campos nuevos, o con ellos en su valor de backfill (modo mensual_pantalla y
//      `fecha_fin` = fin de mes de fecha_inicio + plazo − 1): reproduce EXACTAMENTE lo de F1 (meses
//      completos, "el estado manda"). Es la regla de EQUIVALENCIA: un dato que no aporta información
//      nueva no cambia el resultado.
//    - modo "extendido": hay información nueva → `fecha_fin` distinta del default legado (el servicio
//      termina donde dice, meses completos), y/o prorrata por días del primer y último mes SOLO si
//      `prorrata_por_dias = true` (marcador explícito) o el precio es TOTAL del contrato (que siempre se
//      reparte por días).
//    `forma_facturacion` NO cambia devengo ni `lineas` (legado): alimenta `cobros` y `mesesPorFactura`.
//  * "El estado manda" (2026-08-11): la ventana de meses sale de `getMesesAFacturar` — un negocio en
//    estado activo/100% se sigue devengando y facturando hasta el mes actual inclusive aunque el plazo
//    haya vencido. `getFinContrato` define el fin del plazo firmado. Una sola definición (10.1).
//  * Sin prorrata por días (D3 es de F2+): cada mes devenga completo, como hoy.
//  * El motor calcula SIEMPRE el calendario del negocio; qué estados lo consumen lo decide cada
//    superficie con `elegible` (activo / 100%). Un 75% tiene calendario (para proyección) y
//    `elegible:false`.
//
// Semántica elegida donde las superficies de hoy DIVERGEN entre sí (cada una listada en
// docs/decisiones/2026-10-06-carteleria-rediseno-f1.md): la de la grilla de Facturación
// (`useVFDataLoading`), que es la que ya usa `getMesesAFacturar`. Ver comentarios "DIVERGENCIA".

import { esEstadoActivoFacturable, getFinContrato, getMesesAFacturar } from "../carteleriaFacturacion";

/** Campos del §3 del plano (migración 20261006c). Se leen si están; ver cabecera. */
export const CAMPOS_PREVISTOS_F2 = [
  "fecha_fin",
  "modo_precio_gestion",
  "monto_total_gestion",
  "forma_facturacion",
  "facturacion_cada_n_meses",
  "prorrata_por_dias",
];

export const MODO_LEGADO = "legado";
export const MODO_EXTENDIDO = "extendido";

const pad2 = (n) => String(n).padStart(2, "0");
const num = (v) => {
  const x = parseFloat(v);
  return Number.isFinite(x) ? x : 0;
};
const ymd = (y, m, d) => `${y}-${pad2(m)}-${pad2(d)}`;
const ultimoDia = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const parseYMD = (s) => {
  if (!s) return null;
  const [y, m, d] = String(s)
    .split("T")[0]
    .split("-")
    .map((x) => parseInt(x, 10));
  if (!y || !m) return null;
  return { y, m, d: d || 1 };
};
// "hoy" acepta Date o "YYYY-MM-DD"; se devuelve siempre un Date LOCAL (lo que espera getMesesAFacturar).
const aDateLocal = (hoy) => {
  if (hoy instanceof Date) return hoy;
  const p = parseYMD(hoy);
  if (!p) throw new Error("calendarioNegocio: `hoy` es obligatorio (Date o 'YYYY-MM-DD')");
  return new Date(p.y, p.m - 1, p.d);
};
// Mes k-ésimo (0-based) desde (y, m).
const sumarMeses = (y, m, k) => {
  const idx = y * 12 + (m - 1) + k;
  return { y: Math.floor(idx / 12), m: (idx % 12) + 1 };
};
const claveMes = (y, m) => `${y}-${pad2(m)}`;
const r2 = (x) => Math.round(x * 100) / 100;
// Día absoluto (UTC) de una fecha {y,m,d} para restar fechas sin husos horarios.
const diaAbs = (p) => Math.round(Date.UTC(p.y, p.m - 1, p.d) / 86400000);

const asArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    try {
      const p = JSON.parse(v || "[]");
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
};

/**
 * ¿El mes (y, m) cae dentro de la ventana de contenidos? Ventana abierta por el lado que falta
 * (mismo criterio que `contenidosVigenteEnMes` del ER). DIVERGENCIA: la grilla de Facturación exige
 * AMBAS fechas para limitar; con una sola de las dos nulas la trata como "siempre vigente".
 */
const contenidosVigenteEnMes = (neg, y, m) => {
  const ini = parseYMD(neg.contenidos_fecha_inicio);
  const fin = parseYMD(neg.contenidos_fecha_fin);
  const primero = ymd(y, m, 1);
  if (ini && primero < ymd(ini.y, ini.m, ini.d)) return false;
  if (fin && primero > ymd(fin.y, fin.m, fin.d)) return false;
  return true;
};

const mesFacturacion = (mesesFact, tipo, y, m) =>
  mesesFact.find((e) => e && e.tipo === tipo && String(e.mes) === pad2(m) && String(e.ano) === String(y)) || null;

const normalizarTramo = (e, idx) => ({
  indice: idx,
  monto: num(e && e.monto),
  facturafecha: (e && (e.facturafecha || e.fecha_factura)) || null,
  // Solo `facturafecha` (sin el respaldo `fecha_factura`): lo que la grilla de Facturación marca como facturado.
  facturafechaExplicita: (e && e.facturafecha) || null,
  fechaFacturaEstimada: (e && e.fecha_factura_estimada) || null,
  fechaCobroEstimada: (e && e.fecha_cobro_estimada) || null,
  porcentaje: e && e.porcentaje != null ? num(e.porcentaje) : null,
  facturanumero: (e && e.facturanumero) || null,
  facturafechacobro: (e && e.facturafechacobro) || null,
  plazocobro: (e && e.plazocobro) || null,
});

/**
 * @param {object} negocio fila de `negocios_carteleria` (tal cual la devuelve PostgREST).
 * @param {{hoy: (Date|string), mesesPorFactura?: number}} opts `hoy` es obligatorio: el motor no lee el reloj.
 *   `mesesPorFactura` (opcional) es la periodicidad que la pantalla ya resolvió contra el puente de DB
 *   (`getMesesPorFactura`); el motor es puro y solo puede agrupar por folio de texto.
 */
export const calendarioNegocio = (negocio, { hoy, mesesPorFactura: mesesPorFacturaExterno } = {}) => {
  const n = negocio || {};
  const hoyDate = aDateLocal(hoy);
  const advertencias = [];

  const estado = n.estado || "";
  const elegible = esEstadoActivoFacturable(estado);
  const pais = n.pais || null;
  const esOT = n.tipo_vertical === "OT";
  const pantallas = parseInt(n.cantidad_pantallas, 10) || 1;
  const plazo = parseInt(n.plazo_contrato_meses, 10) || 0;
  const inicio = parseYMD(n.fecha_inicio);
  const modalidad = (n.modalidad || "").toUpperCase();

  const monedaGestion = n.moneda_gestion || null;
  const monedaContenidos = n.moneda_contenidos || n.moneda_gestion || null;
  const monedaHardware = n.moneda_hardware || null;
  if (n.incluye_hardware && !monedaHardware) advertencias.push({ codigo: "moneda_hardware_null" });
  if (!esOT && num(n.fee_gestion_unitario) > 0 && !monedaGestion) advertencias.push({ codigo: "moneda_gestion_null" });
  if (n.incluye_contenidos && !n.moneda_contenidos) advertencias.push({ codigo: "moneda_contenidos_inferida" });

  // ── Campos nuevos (F2) y elección de modo ──────────────────────────────────────────────────────
  // Fin por defecto del modo legado = último día del mes (fecha_inicio + plazo − 1): es el valor de
  // backfill de `fecha_fin`.
  const finLegado =
    inicio && plazo > 0
      ? (() => {
          const f = sumarMeses(inicio.y, inicio.m, plazo - 1);
          return ymd(f.y, f.m, ultimoDia(f.y, f.m));
        })()
      : null;
  let fechaFin = n.fecha_fin ? String(n.fecha_fin).split("T")[0] : null;
  if (fechaFin && inicio && fechaFin < ymd(inicio.y, inicio.m, inicio.d)) {
    advertencias.push({ codigo: "fecha_fin_anterior_al_inicio_ignorada", fecha_fin: fechaFin });
    fechaFin = null;
  }
  const montoTotalGestion = num(n.monto_total_gestion);
  let totalContrato = n.modo_precio_gestion === "total_contrato";
  if (totalContrato && !(montoTotalGestion > 0)) {
    advertencias.push({ codigo: "total_contrato_sin_monto_ignorado" });
    totalContrato = false;
  }
  const finEfectivo = fechaFin || finLegado;
  if (totalContrato && !(inicio && finEfectivo)) {
    advertencias.push({ codigo: "total_contrato_sin_fechas_ignorado" });
    totalContrato = false;
  }
  // EQUIVALENCIA: un `fecha_fin` igual al default legado no aporta información nueva.
  const prorrataPorDias = n.prorrata_por_dias === true;
  const prorratear = totalContrato || prorrataPorDias;
  const extendido = !!inicio && !!finEfectivo && (prorratear || (!!fechaFin && fechaFin !== finLegado));

  let forma = n.forma_facturacion || "mensual";
  let cadaN = parseInt(n.facturacion_cada_n_meses, 10) || null;
  if (!["mensual", "cada_n_meses", "unica"].includes(forma)) {
    advertencias.push({ codigo: "forma_facturacion_invalida_ignorada", forma });
    forma = "mensual";
  }
  if (forma === "cada_n_meses" && !(cadaN >= 2)) {
    advertencias.push({ codigo: "cada_n_meses_sin_n_ignorado" });
    forma = "mensual";
    cadaN = null;
  }

  const feeGestion = esOT ? 0 : num(n.fee_gestion_unitario);
  const feeContenidosBase = !esOT && n.incluye_contenidos ? num(n.fee_contenidos_unitario) : 0;
  const cuotaOpex = n.incluye_hardware && modalidad === "OPEX" ? num(n.cuota_hardware_opex) : 0;

  // Ventana del servicio.
  const fin = extendido ? parseYMD(finEfectivo) : null;
  const spanMeses = extendido ? (fin.y - inicio.y) * 12 + (fin.m - inicio.m) + 1 : 0;
  const plazoEf = extendido ? spanMeses : plazo; // meses del plazo firmado
  let mesesVentana;
  if (extendido) {
    const mesesHastaHoy = (hoyDate.getFullYear() - inicio.y) * 12 + (hoyDate.getMonth() + 1 - inicio.m) + 1;
    // "El estado manda" también acá, pero un precio TOTAL cubre solo su período: sin extensión.
    mesesVentana = !totalContrato && elegible ? Math.max(spanMeses, mesesHastaHoy) : spanMeses;
  } else {
    mesesVentana = inicio && plazo > 0 ? getMesesAFacturar(n.fecha_inicio, plazo, estado, hoyDate) : plazo;
  }

  // Monto mensual por componente, en moneda original, sin ventana ni estado (sirve a totales de plazo).
  // En precio total, el "mensual" es el promedio por mes calendario del período (dato derivado).
  const mensual = {
    gestion: {
      monto: totalContrato ? montoTotalGestion / Math.max(spanMeses, 1) : feeGestion * pantallas,
      moneda: monedaGestion,
      ...(totalContrato ? { derivado: true } : {}),
    },
    contenidos: { monto: feeContenidosBase * pantallas, moneda: monedaContenidos },
    opex: { monto: cuotaOpex * pantallas, moneda: monedaHardware },
  };

  const finPlazo = extendido ? { year: fin.y, month: fin.m } : getFinContrato(n.fecha_inicio, n.plazo_contrato_meses);

  // ── (a) DEVENGO ────────────────────────────────────────────────────────────────────────────────
  const devengo = [];
  if (!extendido) {
    // MODO LEGADO (idéntico a F1): meses completos.
    if (inicio && plazo > 0) {
      for (let i = 0; i < mesesVentana; i++) {
        const { y, m } = sumarMeses(inicio.y, inicio.m, i);
        const periodo = { desde: ymd(y, m, 1), hasta: ymd(y, m, ultimoDia(y, m)) };
        const base = {
          mes: claveMes(y, m),
          ano: y,
          mesNum: m,
          indice: i,
          enPlazo: i < plazo,
          prorroga: i + 1 > plazo ? i + 1 - plazo : 0,
          periodo,
        };
        if (mensual.gestion.monto > 0) {
          devengo.push({ ...base, componente: "gestion", monto: mensual.gestion.monto, moneda: monedaGestion });
        }
        if (mensual.contenidos.monto > 0 && contenidosVigenteEnMes(n, y, m)) {
          devengo.push({
            ...base,
            componente: "contenidos",
            monto: mensual.contenidos.monto,
            moneda: monedaContenidos,
          });
        }
        if (mensual.opex.monto > 0) {
          devengo.push({ ...base, componente: "opex", monto: mensual.opex.monto, moneda: monedaHardware });
        }
      }
    }
  } else {
    // MODO EXTENDIDO: `fecha_fin` propia, prorrata por días (si está marcada) y/o precio total por días.
    const dIni = diaAbs(inicio);
    const dFin = diaAbs(fin);
    const diasTotales = dFin - dIni + 1;
    // Reparto del precio total: redondeo a 2 decimales por mes y el último mes absorbe el residuo
    // (Σ = monto_total_gestion exacto).
    const reparto = [];
    if (totalContrato) {
      let acum = 0;
      for (let i = 0; i < spanMeses; i++) {
        const { y, m } = sumarMeses(inicio.y, inicio.m, i);
        const desde = Math.max(dIni, diaAbs({ y, m, d: 1 }));
        const hasta = Math.min(dFin, diaAbs({ y, m, d: ultimoDia(y, m) }));
        const dias = hasta - desde + 1;
        const parte = i === spanMeses - 1 ? r2(montoTotalGestion - acum) : r2((montoTotalGestion * dias) / diasTotales);
        acum = r2(acum + parte);
        reparto.push(parte);
      }
    }
    for (let i = 0; i < mesesVentana; i++) {
      const { y, m } = sumarMeses(inicio.y, inicio.m, i);
      const diasMes = ultimoDia(y, m);
      const enSpan = i < spanMeses;
      // Sin prorrata (marcador apagado) cada mes del período es completo.
      const parcial = enSpan && prorratear;
      const desdeAbs = parcial ? Math.max(dIni, diaAbs({ y, m, d: 1 })) : diaAbs({ y, m, d: 1 });
      const hastaAbs = parcial ? Math.min(dFin, diaAbs({ y, m, d: diasMes })) : diaAbs({ y, m, d: diasMes });
      const dias = hastaAbs - desdeAbs + 1;
      const fraccion = dias / diasMes;
      const d0 = new Date(desdeAbs * 86400000);
      const d1 = new Date(hastaAbs * 86400000);
      const periodo = {
        desde: ymd(d0.getUTCFullYear(), d0.getUTCMonth() + 1, d0.getUTCDate()),
        hasta: ymd(d1.getUTCFullYear(), d1.getUTCMonth() + 1, d1.getUTCDate()),
      };
      const base = {
        mes: claveMes(y, m),
        ano: y,
        mesNum: m,
        indice: i,
        enPlazo: enSpan,
        prorroga: enSpan ? 0 : i + 1 - spanMeses,
        periodo,
        dias,
        diasMes,
        fraccion,
      };
      const montoGestion = totalContrato ? (enSpan ? reparto[i] : 0) : mensual.gestion.monto * fraccion;
      if (montoGestion > 0)
        devengo.push({ ...base, componente: "gestion", monto: montoGestion, moneda: monedaGestion });
      if (mensual.contenidos.monto > 0 && contenidosVigenteEnMes(n, y, m)) {
        devengo.push({
          ...base,
          componente: "contenidos",
          monto: mensual.contenidos.monto * fraccion,
          moneda: monedaContenidos,
        });
      }
      if (mensual.opex.monto > 0) {
        devengo.push({ ...base, componente: "opex", monto: mensual.opex.monto * fraccion, moneda: monedaHardware });
      }
    }
  }
  const devMap = new Map(devengo.map((d) => [`${d.componente}|${d.mes}`, d]));
  const montoDev = (comp, y, m, fallback) => {
    const d = devMap.get(`${comp}|${claveMes(y, m)}`);
    return d ? d.monto : fallback;
  };

  // ── (b) HARDWARE CAPEX ─────────────────────────────────────────────────────────────────────────
  const tramos = asArray(n.detalle_pagos_hardware).map(normalizarTramo);
  const otros = asArray(n.detalle_otros_hardware).map((e, idx) => ({
    indice: idx,
    monto: num(e && e.monto),
    facturafecha: (e && (e.facturafecha || e.fecha_factura)) || null,
  }));
  // D12 (2026-10-06): un tramo ÚNICO sin monto vale `monto_hardware`. Solo lo ven los `cobros`; `lineas` queda
  // como la grilla de Facturación (que aún no lo aplica).
  tramos.forEach((t) => {
    t.montoEfectivo = t.monto > 0 ? t.monto : tramos.length === 1 ? num(n.monto_hardware) : 0;
  });
  const esCapex = !!n.incluye_hardware && modalidad === "CAPEX";
  const hardwareCapex = esCapex
    ? {
        fecha: n.fecha_cierre || null,
        fechaEfectiva: n.fecha_cierre || n.fecha_inicio || null, // cascada de la grilla de Facturación
        monto: num(n.monto_hardware),
        moneda: monedaHardware,
        tramos,
        otros,
        totalConOtros: num(n.monto_hardware) + otros.reduce((s, o) => s + o.monto, 0),
      }
    : null;

  // ── (c) LÍNEAS DE FACTURACIÓN (granularidad de la grilla: una por mes / tramo) ────────────────────
  const mesesFact = asArray(n.meses_facturacion);
  const lineas = [];

  // Ventana de líneas = ventana de devengo, más cualquier mes ya facturado que caiga después
  // (no perder una fila con match en meses_facturacion; mismo criterio defensivo de la grilla).
  const mesesAGenerar = (tipo) => {
    let total = mesesVentana;
    if (inicio) {
      for (const mf of mesesFact) {
        if (!mf || mf.tipo !== tipo || !mf.mes || !mf.ano) continue;
        const off = (parseInt(mf.ano, 10) - inicio.y) * 12 + (parseInt(mf.mes, 10) - inicio.m);
        if (off + 1 > total) total = off + 1;
      }
    }
    return total;
  };

  if (esCapex) {
    if (tramos.length > 0) {
      tramos.forEach((t) => {
        if (t.monto <= 0) return;
        lineas.push({
          tipo: "hardware_capex",
          clave: { tipo: "hardware", tramo: t.indice },
          mes: (t.facturafecha || t.fechaFacturaEstimada || n.fecha_cierre || n.fecha_inicio || "").slice(0, 7) || null,
          monto: t.monto,
          moneda: monedaHardware,
          etiqueta: `Pago ${t.indice + 1}/${tramos.length}`,
          facturada: !!t.facturafecha,
          facturafecha: t.facturafecha,
          facturafechaExplicita: t.facturafechaExplicita,
          facturanumero: t.facturanumero,
          facturafechacobro: t.facturafechacobro,
          plazocobro: t.plazocobro,
          porcentaje: t.porcentaje,
        });
      });
    } else if (num(n.monto_hardware) > 0) {
      lineas.push({
        tipo: "hardware_capex",
        clave: { tipo: "hardware", tramo: 0 },
        mes: (n.fecha_cierre || n.fecha_inicio || "").slice(0, 7) || null,
        monto: num(n.monto_hardware),
        moneda: monedaHardware,
        etiqueta: "Pago único",
        facturada: false,
        facturafecha: null,
        facturanumero: null,
        facturafechacobro: null,
        plazocobro: null,
        porcentaje: null,
      });
    }
  }

  if (inicio && plazoEf > 0) {
    const etiq = (i) => (i + 1 > plazoEf ? `${plazoEf} +${i + 1 - plazoEf}` : `${i + 1}/${plazoEf}`);
    if (mensual.opex.monto > 0) {
      const total = mesesAGenerar("opex");
      for (let i = 0; i < total; i++) {
        const { y, m } = sumarMeses(inicio.y, inicio.m, i);
        const mf = mesFacturacion(mesesFact, "opex", y, m);
        lineas.push({
          tipo: "opex",
          clave: { tipo: "opex", ano: String(y), mes: pad2(m), tramo: 1 },
          mes: claveMes(y, m),
          indice: i,
          prorroga: i + 1 > plazoEf ? i + 1 - plazoEf : 0,
          monto: montoDev("opex", y, m, mensual.opex.monto),
          moneda: monedaHardware,
          etiqueta: `Mes ${etiq(i)}`,
          facturada: !!mf,
          facturafecha: (mf && mf.facturafecha) || null,
          facturanumero: (mf && mf.facturanumero) || null,
          facturafechacobro: (mf && mf.facturafechacobro) || null,
          plazocobro: (mf && mf.plazocobro) || null,
        });
      }
    }
    if (mensual.gestion.monto > 0 || mensual.contenidos.monto > 0) {
      const total = mesesAGenerar("gestion");
      for (let i = 0; i < total; i++) {
        const { y, m } = sumarMeses(inicio.y, inicio.m, i);
        const mf = mesFacturacion(mesesFact, "gestion", y, m);
        const contVigente = mensual.contenidos.monto > 0 && contenidosVigenteEnMes(n, y, m);
        lineas.push({
          tipo: "gestion",
          clave: { tipo: "gestion", ano: String(y), mes: pad2(m), tramo: 1 },
          mes: claveMes(y, m),
          indice: i,
          prorroga: i + 1 > plazoEf ? i + 1 - plazoEf : 0,
          // Gestión y contenidos van en la MISMA factura pero pueden tener moneda distinta (10.62): se
          // devuelven separados, nunca sumados.
          monto: totalContrato ? montoDev("gestion", y, m, 0) : montoDev("gestion", y, m, mensual.gestion.monto),
          moneda: monedaGestion,
          contenidos: contVigente
            ? { monto: montoDev("contenidos", y, m, mensual.contenidos.monto), moneda: monedaContenidos }
            : null,
          etiqueta: etiq(i),
          facturada: !!mf,
          facturafecha: (mf && mf.facturafecha) || null,
          facturanumero: (mf && mf.facturanumero) || null,
          facturafechacobro: (mf && mf.facturafechacobro) || null,
          plazocobro: (mf && mf.plazocobro) || null,
        });
      }
    }
  }

  // ── (d) COBROS según la forma de facturar (D4) ─────────────────────────────────────────────────
  // `lineas` es la granularidad de la grilla (no cambia con la forma). `cobros` es lo que se factura y
  // cobra de verdad: mensual → uno por mes; cada_n_meses → uno por bloque de N meses; unica → uno solo
  // con todo (D6: en caja un solo cobro; el resultado sigue devengándose mes a mes en `devengo`).
  // Cada cobro lista sus componentes por moneda; `totalesPorMoneda` suma SOLO dentro de una moneda.
  const sumarComponentes = (items) => {
    const por = new Map();
    items.forEach((d) => {
      const k = `${d.componente}|${d.moneda || ""}`;
      const e = por.get(k) || { componente: d.componente, moneda: d.moneda || null, monto: 0, n: 0 };
      e.monto += d.monto;
      e.n += 1;
      por.set(k, e);
    });
    // Un solo ítem (cobro mensual): monto EXACTO, sin redondeo (las superficies convierten y redondean
    // después). Varios ítems (cada N meses, única): suma redondeada a 2 decimales.
    return [...por.values()].map(({ n: cantidad, ...c }) => ({ ...c, monto: cantidad > 1 ? r2(c.monto) : c.monto }));
  };
  const totalesPorMoneda = (componentes) => {
    const t = {};
    componentes.forEach((c) => {
      const k = c.moneda || "SIN_MONEDA";
      t[k] = r2((t[k] || 0) + c.monto);
    });
    return t;
  };
  const mesesDevengo = [...new Set(devengo.map((d) => d.mes))].sort();
  const cobros = [];
  const armarCobro = (tipo, items, extra) => {
    const componentes = sumarComponentes(items);
    return {
      tipo,
      mes: items.length > 0 ? items.map((d) => d.mes).sort()[0] : null,
      periodo:
        items.length > 0
          ? {
              desde: items.map((d) => d.periodo.desde).sort()[0],
              hasta: items
                .map((d) => d.periodo.hasta)
                .sort()
                .at(-1),
            }
          : null,
      componentes,
      totalesPorMoneda: totalesPorMoneda(componentes),
      ...extra,
    };
  };
  if (forma === "unica") {
    const componentes = sumarComponentes(devengo);
    if (esCapex) {
      const hw = hardwareCapex.totalConOtros;
      if (hw > 0) componentes.push({ componente: "hardware_capex", moneda: monedaHardware, monto: hw });
    }
    if (componentes.length > 0) {
      cobros.push({
        tipo: "unica",
        // Sin documento aún: la fecha estimada es la del cierre del hardware si lo hay, si no el inicio.
        fecha: (esCapex && n.fecha_cierre) || n.fecha_inicio || null,
        fechaEstimada: true,
        mes: ((esCapex && n.fecha_cierre) || n.fecha_inicio || "").slice(0, 7) || null,
        periodo:
          devengo.length > 0
            ? {
                desde: devengo.map((d) => d.periodo.desde).sort()[0],
                hasta: devengo
                  .map((d) => d.periodo.hasta)
                  .sort()
                  .at(-1),
              }
            : null,
        componentes,
        totalesPorMoneda: totalesPorMoneda(componentes),
      });
    }
  } else {
    const bloque = forma === "cada_n_meses" ? cadaN : 1;
    for (let k = 0; k < mesesDevengo.length; k += bloque) {
      const meses = new Set(mesesDevengo.slice(k, k + bloque));
      const items = devengo.filter((d) => meses.has(d.mes));
      cobros.push(
        armarCobro(forma === "cada_n_meses" ? "cada_n_meses" : "mensual", items, { mesesCubiertos: meses.size })
      );
    }
    if (esCapex) {
      const hw = (monto, extra) => {
        const comp = [{ componente: "hardware_capex", moneda: monedaHardware, monto }];
        cobros.push({
          tipo: "hardware_capex",
          periodo: null,
          componentes: comp,
          totalesPorMoneda: totalesPorMoneda(comp),
          ...extra,
        });
      };
      if (tramos.length > 0) {
        tramos
          .filter((t) => t.montoEfectivo > 0)
          .forEach((t) =>
            hw(t.montoEfectivo, {
              tramo: t.indice,
              mes:
                (t.facturafecha || t.fechaFacturaEstimada || n.fecha_cierre || n.fecha_inicio || "").slice(0, 7) ||
                null,
              etiqueta: `Pago ${t.indice + 1}/${tramos.length}`,
              facturada: !!t.facturafecha,
            })
          );
      } else if (num(n.monto_hardware) > 0) {
        hw(num(n.monto_hardware), {
          tramo: 0,
          mes: (n.fecha_cierre || n.fecha_inicio || "").slice(0, 7) || null,
          etiqueta: "Pago único",
          facturada: false,
        });
      }
    }
  }

  // Meses que cubre cada factura de gestión (periodicidad). Con forma explícita manda la forma; en
  // modo legado/mensual se agrupa por folio de texto (el puente `facturas_sii_vinculaciones` es de DB y
  // el motor es puro) — equivale a `getMesesPorFactura` salvo en el alcance 2026-con-documento (F1).
  const porFolio = {};
  mesesFact
    .filter((e) => e && e.tipo === "gestion" && e.facturanumero)
    .forEach((e) => {
      const k = String(e.facturanumero);
      porFolio[k] = (porFolio[k] || 0) + 1;
    });
  const cuentas = Object.values(porFolio);
  const mesesPorFacturaFolio = cuentas.length > 0 ? Math.max(...cuentas) : 1;
  const mesesPorFactura =
    forma === "cada_n_meses" ? cadaN : forma === "unica" ? Math.max(mesesDevengo.length, 1) : mesesPorFacturaFolio;

  const mesesPorFacturaFinal =
    Number.isFinite(mesesPorFacturaExterno) && mesesPorFacturaExterno >= 1 ? mesesPorFacturaExterno : mesesPorFactura;

  // ── (e) TOTALES para las pantallas de resumen (F3a): todo en moneda original, sin convertir ────────
  // `meses` = plazo firmado (|| 1 para un OT sin plazo, como la tabla de hoy). Los totales de plazo NO
  // dependen del estado ni de la ventana de devengo ("el estado manda" es de devengo/facturación).
  const mesesContrato = (extendido ? spanMeses : plazo) || 1;
  const otrosTotal = otros.reduce((s, o) => s + o.monto, 0);
  const capexLegado = modalidad === "CAPEX" ? num(n.monto_hardware) : 0; // la tabla de hoy no exige incluye_hardware
  const totalPlazo = {
    meses: mesesContrato,
    gestion: {
      monto: totalContrato ? montoTotalGestion : mensual.gestion.monto * mesesContrato,
      moneda: monedaGestion,
    },
    contenidos: { monto: mensual.contenidos.monto * mesesContrato, moneda: monedaContenidos },
    opex: { monto: mensual.opex.monto * mesesContrato, moneda: monedaHardware },
    hardwareCapex: {
      monto: capexLegado,
      otros: modalidad === "CAPEX" ? otrosTotal : 0,
      total: capexLegado + (modalidad === "CAPEX" ? otrosTotal : 0),
      moneda: monedaHardware,
    },
  };
  // Lo que se factura por cada factura según la periodicidad (mensual × meses por factura).
  const porFactura = {
    meses: mesesPorFacturaFinal,
    gestion: { monto: mensual.gestion.monto * mesesPorFacturaFinal, moneda: monedaGestion },
    contenidos: { monto: mensual.contenidos.monto * mesesPorFacturaFinal, moneda: monedaContenidos },
    opex: { monto: mensual.opex.monto * mesesPorFacturaFinal, moneda: monedaHardware },
  };
  // Valores UNITARIOS (por pantalla, por mes) tal como se cargaron: son dato, no cálculo.
  const porPantalla = {
    gestion: { monto: feeGestion, moneda: monedaGestion },
    contenidos: { monto: feeContenidosBase, moneda: monedaContenidos },
    opex: { monto: cuotaOpex, moneda: monedaHardware },
  };

  return {
    modo: extendido ? MODO_EXTENDIDO : MODO_LEGADO,
    negocioId: n.id != null ? String(n.id) : null,
    pais,
    estado,
    elegible,
    tipoVertical: n.tipo_vertical || null,
    modalidad: modalidad || null,
    pantallas,
    plazo,
    fechaInicio: n.fecha_inicio || null,
    fechaFin: extendido ? finEfectivo : fechaFin,
    prorrataPorDias: prorratear,
    finPlazo: finPlazo ? claveMes(finPlazo.year, finPlazo.month) : null,
    mesesVentana,
    vencido:
      !!finPlazo &&
      (finPlazo.year < hoyDate.getFullYear() ||
        (finPlazo.year === hoyDate.getFullYear() && finPlazo.month < hoyDate.getMonth() + 1)),
    mensual,
    precioGestion: totalContrato
      ? { modo: "total_contrato", monto: montoTotalGestion, moneda: monedaGestion }
      : { modo: "mensual_pantalla", monto: feeGestion, moneda: monedaGestion, porPantalla: true },
    formaFacturacion: { forma, cadaNMeses: forma === "cada_n_meses" ? cadaN : null },
    devengo,
    hardwareCapex,
    lineas,
    cobros,
    mesesPorFactura: mesesPorFacturaFinal,
    incluyeHardware: !!n.incluye_hardware,
    incluyeContenidos: !!n.incluye_contenidos,
    monedas: {
      gestion: monedaGestion,
      contenidos: monedaContenidos,
      contenidosCargada: n.moneda_contenidos || null,
      hardware: monedaHardware,
    },
    totalPlazo,
    porFactura,
    porPantalla,
    previstos: Object.fromEntries(CAMPOS_PREVISTOS_F2.map((c) => [c, n[c] != null ? n[c] : null])),
    advertencias,
  };
};

// ── Consultas derivadas (puras, sobre el calendario ya calculado) ──────────────────────────────────

/** Σ del devengo de un componente en un mes "YYYY-MM" (moneda original; null si no hay devengo). */
export const devengoDelMes = (cal, componente, mes) => {
  const items = (cal.devengo || []).filter((d) => d.componente === componente && d.mes === mes);
  if (items.length === 0) return null;
  return { monto: items.reduce((s, d) => s + d.monto, 0), moneda: items[0].moneda };
};

/** Mes "YYYY-MM" en que cae el evento CAPEX (cascada `fecha_cierre` → `fecha_inicio`), o null. */
export const mesHardwareCapex = (cal) =>
  cal.hardwareCapex && cal.hardwareCapex.fechaEfectiva ? cal.hardwareCapex.fechaEfectiva.slice(0, 7) : null;
